<template>
  <v-select
      ref="users-select"
      :class="{'small': small}"
      v-bind:options="users"
      :clearable="clearable"
      :multiple="multiple"
      :reduce="u=>u.id"
      label="id"
      v-bind:appendToBody="appendToBody"
      v-model="selected"
      :disabled="disabled"
      :filterable="false"
      :selectable="option => !option._isGroup"
      @open="onOpen"
      @search="onSearch"
  >
    <template #option="user">
      <div v-if="user._isGroup" class="user-picker-group-header">
        {{ user.name }}
      </div>
      <div v-else class="text-capitalize user-picker-option">
        <user-avatar size="25" variant="info" :userId="user.id"></user-avatar>
        {{ getUserFullName(user.id) }}
      </div>
    </template>
    <template #selected-option="user">
      <div class="text-capitalize">
        <user-avatar size="15" variant="info" :userId="user.id"></user-avatar>
        {{ getUserFullName(user.id) }}
      </div>
    </template>
  </v-select>
</template>

<script>
import { mapGetters } from 'vuex'
import UserAvatar from '@/components/widget/UserAvatar'

export default {
  name: 'userPicker',
  components: { UserAvatar },
  props: {
    value: {
      required: true,
    },
    orgId: {
      type: String,
      default: null,
    },
    small: {
      type: Boolean,
      default: () => false,
    },
    selectProps: {
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    clearable: {
      type: Boolean,
      default: () => false,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
    excludeMe: {
      type: Boolean,
      default: () => false,
    },
    appendToBody: {
      type: Boolean,
      default: () => false,
    },
    // ✅ Both contexts supported
    clientId: {
      type: String,
      default: null,
    },
    monetaryId: {
      type: String,
      default: null,
    },
    userType: {
      type: Number,
      default: null,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    users: [],
    // The full (grouped) option list is expensive to build, so it is only
    // assembled the first time the dropdown is opened. Until then `users`
    // holds just the selected user(s) so the chip renders correctly. This
    // keeps large tables (hundreds of rows, each with a picker) fast.
    usersLoaded: false,
  }),
  computed: {
    ...mapGetters('user', ['myId']),
    ...mapGetters('data', ['allUsers', 'getUserFullName', 'getBusinessUnitById', 'allBusinessUnits']),
    selected: {
      get() {
        return this.value
      },
      set(value) {
        if (value !== this.value && value != null) {
          // ✅ Handle both client & monetary context
          if (this.clientId) {
            this.updateRepresentativeId(this.clientId, value)
          } else if (this.monetaryId) {
            this.updateDepositorId(this.monetaryId, value)
          }
        }
        this.$emit('input', value)
      },
    },
  },
  created() {
    this.buildSelectedUsers()
  },
  watch: {
    value() {
      // When the row's value changes externally and the full list hasn't been
      // built yet, refresh the lightweight selected-only list so the chip stays
      // in sync without paying for the full grouped build.
      if (!this.usersLoaded) {
        this.buildSelectedUsers()
      }
    },
  },
  methods: {
    // Lightweight list containing only the currently selected user(s); enough
    // for vue-select to render the selected chip, but skips the costly grouping.
    buildSelectedUsers() {
      const ids = Array.isArray(this.value)
          ? this.value
          : (this.value != null ? [this.value] : [])
      this.users = this.allUsers
          .filter(u => ids.includes(u.id))
          .map(u => ({
            ...u,
            name: u.firstName + ' ' + (u.lastName || ''),
            value: u.id,
          }))
    },
    // Build the full grouped list the first time the dropdown is opened.
    onOpen() {
      if (!this.usersLoaded) {
        this.refreshUsers()
        this.usersLoaded = true
      }
    },
    refreshUsers() {
      let $this = this;
      let users = this.allUsers.map(u => ({
        ...u,
        name: u.firstName + ' ' + (u.lastName || ''),
        value: u.id,
        businessUnits: u.businessUnits || [],
        orgIds: u.orgIds || [],
      }))

      if (this.excludeMe) {
        users = users.filter(u => u.id !== this.myId)
      }
      if(this.orgId) {
        users = users.filter(u => u.orgIds.includes(this.orgId))
      }

      if (this.search && this.search.trim() !== '') {
        const words = this.search.trim().toLowerCase().split(' ')
        users = users.filter(u => {
          // Always include selected user(s)
          if (Array.isArray(this.selected) && this.selected.includes(u.id)) return true
          if (!Array.isArray(this.selected) && this.selected === u.id) return true

          // Match search
          return words.some(
              w =>
                  (u.firstName || '').toLowerCase().includes(w) ||
                  (u.lastName || '').toLowerCase().includes(w) ||
                  (u.email || '').toLowerCase().includes(w)
          )
        })
      }

      const allBuIds = new Set(this.allBusinessUnits.filter(bu => $this.orgId ? $this.orgId === bu.orgId : true  ).map(bu => bu.id))
      const NO_BU = '__no_bu__'
      const groups = new Map()
      users.forEach(u => {
        const buId = (u.businessUnits && u.businessUnits.find(b =>allBuIds.has(b))) || NO_BU
        if(buId === NO_BU && $this.orgId) return; // If user has no BU in the org, exclude them from the list
        if (!groups.has(buId)) groups.set(buId, [])
        groups.get(buId).push(u)
      })

      const buLabel = id =>
          id === NO_BU ? 'No Business Unit' : (this.getBusinessUnitById(id).name || 'Unknown')

      const sortedGroupIds = Array.from(groups.keys()).sort((a, b) => {
        if (a === NO_BU) return 1
        if (b === NO_BU) return -1
        return buLabel(a).localeCompare(buLabel(b))
      })

      const grouped = []
      sortedGroupIds.forEach(buId => {
        grouped.push({
          id: `__group__${buId}`,
          _isGroup: true,
          name: buLabel(buId),
        })
        groups.get(buId)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .forEach(u => grouped.push(u))
      })

      if (this.nullable) {
        grouped.unshift({
          id: null,
          value: null,
          name: 'NOT ASSIGNED',
        })
      }

      this.users = grouped
    },

    updateRepresentativeId(clientId, userId) {
      this.$emit('update', {
        clientId,
        userId,
        userType: this.userType,
      })
    },

    updateDepositorId(monetaryId, userId) {
      this.$emit('update', {
        monetaryId,
        userId,
      })
    },

    onSearch(search, loading) {
      loading(true)
      this.search = search
      this.refreshUsers()
      loading(false)
    },
  },
}
</script>

<style scoped>
.small {
  font-size: 0.85rem;
}
.user-picker-group-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  padding: 4px 0;
  pointer-events: none;
  cursor: default;
}
.user-picker-option {
  padding-left: 0.5rem;
}
</style>
